import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import BuildIcon from '@mui/icons-material/BuildRounded';
import BlockIcon from '@mui/icons-material/BlockRounded';
import InfoIcon from '@mui/icons-material/InfoRounded';
import UpdateIcon from '@mui/icons-material/SystemUpdateAltRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';

const DEFAULT_POLL_MS = 60_000;

const BANNER_HEIGHT = 40;

const MaintenanceBanner = () => {
  const { t } = useTranslation();
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  const TYPE_CONFIG = {
    maintenance: {
      bg: '#e65100',
      icon: <BuildIcon sx={{ fontSize: 20, mr: 1 }} />,
      label: t('maintenanceBanner.maintenance'),
    },
    update: {
      bg: '#ef6c00',
      icon: <UpdateIcon sx={{ fontSize: 20, mr: 1 }} />,
      label: t('maintenanceBanner.update'),
    },
    closed: {
      bg: '#c62828',
      icon: <BlockIcon sx={{ fontSize: 20, mr: 1 }} />,
      label: t('maintenanceBanner.closed'),
    },
    info: {
      bg: '#1565c0',
      icon: <InfoIcon sx={{ fontSize: 20, mr: 1 }} />,
      label: t('maintenanceBanner.info'),
    },
  };

  useEffect(() => {
    let cancelled = false;
    let interval;
    let currentMs = DEFAULT_POLL_MS;

    const check = async () => {
      try {
        const res = await fetch('/api/maintenance-status');
        if (res.ok && !cancelled) {
          const data = await res.json();
          setBanners(data.banners || []);
          // Clear dismissed for banners that no longer exist
          setDismissed((prev) => {
            const activeIds = new Set((data.banners || []).map(b => b.id));
            const next = new Set([...prev].filter(id => activeIds.has(id)));
            return next.size !== prev.size ? next : prev;
          });
          // Adjust poll interval from server
          const serverMs = (data.pollInterval || 60) * 1000;
          if (serverMs !== currentMs) {
            currentMs = serverMs;
            clearInterval(interval);
            interval = setInterval(check, currentMs);
          }
        }
      } catch {
        // silent
      }
    };

    check();
    interval = setInterval(check, currentMs);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const visible = banners.filter(b => !dismissed.has(b.id));
  if (visible.length === 0) return null;

  const dismiss = (id) => setDismissed((prev) => new Set([...prev, id]));

  return (
    <>
      <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1300 }}>
        {visible.map((banner) => {
          const config = TYPE_CONFIG[banner.type] || TYPE_CONFIG.maintenance;
          const bg = banner.color || config.bg;
          return (
            <Box
              key={banner.id}
              sx={{
                bgcolor: bg,
                color: '#fff',
                py: 1,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: BANNER_HEIGHT,
                position: 'relative',
              }}
            >
              {config.icon}
              <Typography variant="body2" sx={{ fontWeight: 600, mr: 0.5 }}>
                {config.label}:
              </Typography>
              <Typography variant="body2">
                {banner.message}
              </Typography>
              <IconButton
                size="small"
                onClick={() => dismiss(banner.id)}
                sx={{ color: 'rgba(255,255,255,0.7)', position: 'absolute', right: 8, '&:hover': { color: '#fff' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
      </Box>
      {/* Spacer so fixed banners don't cover page content */}
      <Box sx={{ height: visible.length * BANNER_HEIGHT }} />
    </>
  );
};

export default MaintenanceBanner;
